export type BrowserLocationFailure = "denied" | "unavailable" | "timeout" | "unsupported" | "unknown";

export class BrowserLocationError extends Error {
  constructor(readonly reason: BrowserLocationFailure) {
    super(`browser_location_${reason}`);
    this.name = "BrowserLocationError";
  }
}

type LocationHost = {
  geolocation?: Pick<Geolocation, "getCurrentPosition">;
  permissions?: { query(descriptor:{ name:"geolocation" }):Promise<{ state:PermissionState }> };
};

function rounded(value: number) {
  return Math.round(value * 1_000) / 1_000;
}

function failureFromPositionError(error: Pick<GeolocationPositionError, "code">): BrowserLocationFailure {
  if (error.code === 1) return "denied";
  if (error.code === 2) return "unavailable";
  if (error.code === 3) return "timeout";
  return "unknown";
}

export async function requestRoundedBrowserLocation(host: LocationHost) {
  if (!host.geolocation) throw new BrowserLocationError("unsupported");
  let permission:PermissionState | "unknown" = "unknown";
  if (host.permissions) {
    try {
      permission = (await host.permissions.query({ name:"geolocation" })).state;
      if (permission === "denied") throw new BrowserLocationError("denied");
    } catch (error) {
      if (error instanceof BrowserLocationError) throw error;
      permission = "unknown";
    }
  }
  const position = await new Promise<GeolocationPosition>((resolve, reject) => {
    host.geolocation!.getCurrentPosition(
      resolve,
      (error) => reject(new BrowserLocationError(failureFromPositionError(error))),
      { enableHighAccuracy:false, timeout:8_000, maximumAge:300_000 },
    );
  });
  return {
    latitude:rounded(position.coords.latitude),
    longitude:rounded(position.coords.longitude),
    permission,
  };
}

export function locationErrorCopy(error: unknown) {
  const reason = error instanceof BrowserLocationError ? error.reason : "unknown";
  if (reason === "unsupported") return "이 기기에서는 현재 위치를 읽을 수 없어요. 동네를 직접 골라 주세요.";
  if (reason === "denied") return "위치 권한을 사용하지 않았어요. 아래 동네를 골라도 같은 기준으로 검색할 수 있어요.";
  if (reason === "timeout") return "위치 확인 시간이 지났어요. 다시 시도하거나 아래 동네를 직접 골라 주세요.";
  if (reason === "unavailable") return "현재 위치를 확인할 수 없어요. 잠시 후 다시 시도하거나 아래 동네를 골라 주세요.";
  return "현재 위치 확인 중 문제가 생겼어요. 아래 동네를 골라도 같은 기준으로 검색할 수 있어요.";
}
